/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import { Card, CardContent, Typography } from "@mui/material";
import { DocumentItem, FileExtensionCount } from "../../types/DocumentItem";
import { getFileExtensionCount } from "../../services/auditLogService";
import { useUser } from "../../context/UserContext";
import useApi from "../../hooks/useApi";
import { RootState } from "../../redux/store";
import { useDispatch, useSelector } from "react-redux";
import { clearRecentViewedFile } from "../../redux/recentViewedSlice";
import { FILE_CATEGORIES, ITEM_TITLES, SYSTEMSETTINGS } from "../../data/constants";
import { useSettingValue } from "../../services/useSettingValue";

const initialStorageItems: DocumentItem[] = [
  { icon: "/notes.svg", title: "Documents", count: 0, bgClass: "primary-bg" },
  { icon: "/picture.svg", title: "Pictures", count: 0, bgClass: "secondary-bg" },
  { icon: "/video.svg", title: "Videos", count: 0, bgClass: "primary-bg" },
  { icon: "/audio.svg", title: "Audios", count: 0, bgClass: "secondary-bg" },
];

// Extensions grouped into categories
const fileCategories: Record<string, string> = {
  jpg: "image",
  jpeg: "image",
  png: "image",
  gif: "image",
  svg: "image",
  mp4: "video",
  avi: "video",
  mov: "video",
  mkv: "video",
  mp3: "audio",
  m4a: "audio",
  wav: "audio",
  flac: "audio",
  pdf: "document",
  doc: "document",
  docx: "document",
  txt: "document",
  xlsx: "document",
  xls: "document",
  csv: "document",
};

const DocumentOverview: React.FC = () => {
  const [storageItems, setStorageItems] = useState<DocumentItem[]>(initialStorageItems);
  const { triggerFile } = useSelector((state: RootState) => state.recentViewedUpdated);
  const { userId } = useUser();
  const { executeApi } = useApi();
  const dispatch = useDispatch();
  const fileCategoriesFromDb = useSettingValue(SYSTEMSETTINGS.FILECATEGORIES);
  const fileCategoriesSetting: Record<string, string> = JSON.parse(fileCategoriesFromDb || "{}");

  const loadFileCounts = async () => {
    const fileCounts = await executeApi<number, FileExtensionCount[]>(
      getFileExtensionCount,
      userId
    );
    if (fileCounts && fileCounts.length > 0) {
      const updatedStorageItems = initialStorageItems.map((item) => {
        let totalCount = 0;

        // Check if any extensions belong to the category
        fileCounts.forEach(({ fileExtension, fileCount }) => {
          const category =
            fileCategoriesSetting[fileExtension] ??
            fileCategories[fileExtension] ??
            FILE_CATEGORIES.DOCUMENT; // Map extension to category
          if (
            (item.title === ITEM_TITLES.DOCUMENTS && category === FILE_CATEGORIES.DOCUMENT) ||
            (item.title === ITEM_TITLES.PICTURES && category === FILE_CATEGORIES.IMAGE) ||
            (item.title === ITEM_TITLES.VIDEOS && category === FILE_CATEGORIES.VIDEO) ||
            (item.title === ITEM_TITLES.AUDIOS && category === FILE_CATEGORIES.AUDIO)
          ) {
            totalCount += fileCount;
          }
        });

        return { ...item, count: totalCount };
      });

      setStorageItems(updatedStorageItems);
    }
  };

  useEffect(() => {
    if (userId) {
      loadFileCounts();
    }
  }, [userId]);

  useEffect(() => {
    if (triggerFile > -1) {
      loadFileCounts();
      dispatch(clearRecentViewedFile());
    }
  }, [triggerFile]);

  return (
    <section className="storage-overview mb-3">
      <div className="storage-overview-wrap">
        {storageItems.map((item, index) => (
          <div key={index}>
            <Card>
              <CardContent>
                <div className="storage-overview-item">
                  <div className="storage-overview-img">
                    <div
                      className={`circle rounded-circle d-flex align-items-center justify-content-center ${item.bgClass}`}
                    >
                      <img src={item.icon} alt={`${item.title}-icon`} />
                    </div>
                  </div>
                  <div className="storage-overview-detail">
                    <div>
                      <Typography variant="h5" component="div">
                        {item.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {item.count} Files
                      </Typography>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        ))}
      </div>
    </section>
  );
};

export default DocumentOverview;
